import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { JSX } from 'react';
import { CloseOutlined, PauseOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { Button, Spin, Tooltip } from 'antd';

import {
  youtubePlayerManager,
  type YoutubePlayerState
} from '../../lib/youtube/YoutubePlayerManager';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  selectYoutubeAutoplayBlocked,
  selectYoutubeIsActive,
  selectYoutubePlaybackError,
  selectYoutubePlaybackMatch,
  selectYoutubePlaybackPosition,
  selectYoutubePlaybackStatus,
  selectYoutubeShouldAutoplay,
  youtubePlaybackActions
} from '../../store/slices/youtubePlayback';
import type { YoutubePlaybackMatch } from '../../store/slices/youtubePlayback';
import { formatYoutubeTimestamp } from '../../types/youtubeMatches';
import { trackAnalyticsEvent } from '../../utils/analytics';
import { playerService } from '../../services/player';

const {
  youtubePlaybackStarted,
  youtubePlaybackPaused,
  youtubePlaybackEnded,
  stopYoutubePlayback,
  setYoutubePlaybackPosition,
  setYoutubePlaybackDuration,
  setYoutubeAutoplayBlocked,
  setYoutubePlaybackError
} = youtubePlaybackActions;

const buildAnalyticsPayload = (
  match: YoutubePlaybackMatch,
  extras: Record<string, unknown> = {}
) => ({
  matchId: match.id,
  youtubeVideoId: match.youtubeVideoId,
  workId: match.workId,
  startMs: match.startMs ?? 0,
  endMs: match.endMs ?? null,
  ...extras
});

const formatTime = (value: number | null | undefined): string =>
  formatYoutubeTimestamp(typeof value === 'number' && Number.isFinite(value) ? value : 0);

const YoutubeMiniPlayer = (): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const match = useAppSelector(selectYoutubePlaybackMatch);
  const status = useAppSelector(selectYoutubePlaybackStatus);
  const positionMs = useAppSelector(selectYoutubePlaybackPosition);
  const autoplayBlocked = useAppSelector(selectYoutubeAutoplayBlocked);
  const error = useAppSelector(selectYoutubePlaybackError);
  const shouldAutoplay = useAppSelector(selectYoutubeShouldAutoplay);
  const youtubeIsActive = useAppSelector(selectYoutubeIsActive);
  const spotifyState = useAppSelector((state) => state.spotify.state);
  const spotifyIsPlaying = spotifyState ? !spotifyState.paused : false;

  const matchRef = useRef<YoutubePlaybackMatch | null>(match);
  const lastPositionRef = useRef<number>(positionMs);
  const lastStateRef = useRef<YoutubePlayerState | null>(null);
  const lastAutoplayBlockedRef = useRef<boolean>(autoplayBlocked);
  const wasSpotifyPlayingRef = useRef<boolean>(spotifyIsPlaying);

  useEffect(() => {
    matchRef.current = match;
    lastStateRef.current = null;
  }, [match]);

  useEffect(() => {
    lastPositionRef.current = positionMs;
  }, [positionMs]);

  useEffect(() => {
    lastAutoplayBlockedRef.current = autoplayBlocked;
  }, [autoplayBlocked]);

  useEffect(() => {
    wasSpotifyPlayingRef.current = spotifyIsPlaying;
  }, [spotifyIsPlaying]);

  useEffect(() => {
    if (!match || !containerRef.current) return;

    let disposed = false;
    let rafId: number | null = null;

    const loadMatch = async () => {
      try {
        const container = containerRef.current;
        if (!container || disposed) return;
        youtubePlayerManager.attach(container);
        await youtubePlayerManager.loadMatch(match, {
          autoplay: shouldAutoplay,
          muted: false
        });
      } catch (err) {
        if (disposed) return;
        dispatch(
          setYoutubePlaybackError({
            code: 'load_failed',
            message: err instanceof Error ? err.message : 'Unable to start playback.'
          })
        );
      }
    };

    const schedule =
      typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame
        : (cb: FrameRequestCallback) => window.setTimeout(cb, 0);
    const cancel =
      typeof window.cancelAnimationFrame === 'function'
        ? window.cancelAnimationFrame
        : window.clearTimeout;

    rafId = schedule(() => {
      if (!disposed) {
        void loadMatch();
      }
    });

    return () => {
      disposed = true;
      if (rafId !== null) {
        cancel(rafId);
      }
    };
  }, [dispatch, match, match?.id, shouldAutoplay]);

  useEffect(() => {
    if (match) return;
    youtubePlayerManager.stop();
  }, [match]);

  useEffect(() => {
    const unsubscribeState = youtubePlayerManager.on('stateChange', ({ state, matchId }) => {
      if (!matchRef.current || matchRef.current.id !== matchId) return;

      const previous = lastStateRef.current;
      lastStateRef.current = state;

      if (state === 'playing') {
        dispatch(youtubePlaybackStarted({ positionMs: lastPositionRef.current }));
        if (previous !== 'playing') {
          trackAnalyticsEvent(
            'youtube_playback_start',
            buildAnalyticsPayload(matchRef.current, {
              muted: false
            })
          );
        }
        void playerService.pausePlayback().catch(() => undefined);
      } else if (state === 'paused') {
        dispatch(youtubePlaybackPaused({ positionMs: lastPositionRef.current }));
      } else if (state === 'ended') {
        dispatch(youtubePlaybackEnded({ positionMs: lastPositionRef.current }));
      }
    });

    const unsubscribeProgress = youtubePlayerManager.on(
      'progress',
      ({ positionMs: nextPosition, durationMs: nextDuration, matchId }) => {
        if (!matchRef.current || matchRef.current.id !== matchId) return;
        lastPositionRef.current = nextPosition;
        dispatch(setYoutubePlaybackPosition(nextPosition));
        dispatch(setYoutubePlaybackDuration(nextDuration ?? null));
      }
    );

    const unsubscribeEnded = youtubePlayerManager.on('ended', ({ reason, matchId }) => {
      if (!matchRef.current || matchRef.current.id !== matchId) return;
      const currentMatch = matchRef.current;
      const watched = Math.max(
        0,
        Math.round(lastPositionRef.current - (currentMatch.startMs ?? 0))
      );
      trackAnalyticsEvent(
        'youtube_playback_end',
        buildAnalyticsPayload(currentMatch, {
          reason,
          positionMs: lastPositionRef.current,
          durationWatched: watched
        })
      );
    });

    const unsubscribeAutoplay = youtubePlayerManager.on('autoplayBlocked', ({ matchId }) => {
      if (!matchRef.current || matchRef.current.id !== matchId) return;
      if (!lastAutoplayBlockedRef.current) {
        trackAnalyticsEvent(
          'youtube_autoplay_blocked',
          buildAnalyticsPayload(matchRef.current, {
            positionMs: lastPositionRef.current
          })
        );
      }
      dispatch(setYoutubeAutoplayBlocked(true));
    });

    const unsubscribeError = youtubePlayerManager.on('error', ({ code, message, matchId }) => {
      if (!matchRef.current || matchRef.current.id !== matchId) return;
      dispatch(
        setYoutubePlaybackError({
          code: String(code),
          message
        })
      );
      trackAnalyticsEvent(
        'youtube_playback_error',
        buildAnalyticsPayload(matchRef.current, {
          code,
          message
        })
      );
    });

    return () => {
      unsubscribeState();
      unsubscribeProgress();
      unsubscribeEnded();
      unsubscribeAutoplay();
      unsubscribeError();
    };
  }, [dispatch]);

  useEffect(() => {
    if (!youtubeIsActive) {
      wasSpotifyPlayingRef.current = spotifyIsPlaying;
      return;
    }

    const spotifyJustStarted = spotifyIsPlaying && !wasSpotifyPlayingRef.current;

    if (spotifyJustStarted && matchRef.current) {
      const currentMatch = matchRef.current;
      const watched = Math.max(
        0,
        Math.round(lastPositionRef.current - (currentMatch.startMs ?? 0))
      );
      trackAnalyticsEvent(
        'youtube_playback_end',
        buildAnalyticsPayload(currentMatch, {
          reason: 'spotify_resume',
          positionMs: lastPositionRef.current,
          durationWatched: watched
        })
      );
      youtubePlayerManager.stop();
      dispatch(stopYoutubePlayback());
    }

    wasSpotifyPlayingRef.current = spotifyIsPlaying;
  }, [dispatch, spotifyIsPlaying, youtubeIsActive]);

  useEffect(
    () => () => {
      youtubePlayerManager.stop();
    },
    []
  );

  const handleTogglePlay = useCallback(() => {
    if (!matchRef.current) return;
    if (status === 'loading') return;
    if (status === 'playing') {
      youtubePlayerManager.pause();
      return;
    }

    dispatch(setYoutubeAutoplayBlocked(false));
    youtubePlayerManager.play();
  }, [dispatch, status]);

  const handleClose = useCallback(() => {
    if (matchRef.current) {
      const currentMatch = matchRef.current;
      trackAnalyticsEvent(
        'youtube_match_stopped',
        buildAnalyticsPayload(currentMatch, {
          positionMs: lastPositionRef.current
        })
      );
      const watched = Math.max(
        0,
        Math.round(lastPositionRef.current - (currentMatch.startMs ?? 0))
      );
      trackAnalyticsEvent(
        'youtube_playback_end',
        buildAnalyticsPayload(currentMatch, {
          reason: 'user_stop',
          positionMs: lastPositionRef.current,
          durationWatched: watched
        })
      );
    }
    youtubePlayerManager.stop();
    dispatch(stopYoutubePlayback());
  }, [dispatch]);

  const handleAutoplayResume = useCallback(() => {
    dispatch(setYoutubeAutoplayBlocked(false));
    youtubePlayerManager.play();
  }, [dispatch]);

  const title = match?.performerCredit ?? 'YouTube playback';
  const timingLabel = useMemo(() => {
    if (!match) return '';
    const start = formatTime(match.startMs);
    const end =
      typeof match.endMs === 'number' && Number.isFinite(match.endMs)
        ? ` – ${formatTime(match.endMs)}`
        : '';
    return `${start}${end}`;
  }, [match]);

  const showSpinner = status === 'loading';

  useEffect(() => {
    const rootEl = document.documentElement;
    if (!match) {
      rootEl.style.removeProperty('--youtube-mini-player-height');
      return;
    }
    const element = rootRef.current;
    if (!element) return;

    const updateHeight = () => {
      const rect = element.getBoundingClientRect();
      rootEl.style.setProperty('--youtube-mini-player-height', `${Math.max(0, rect.height)}px`);
    };

    updateHeight();

    let resizeObserver: ResizeObserver | null = null;
    let cleanupFallback: (() => void) | undefined;

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateHeight();
      });
      resizeObserver.observe(element);
    } else {
      const handleResize = () => updateHeight();
      window.addEventListener('resize', handleResize);
      cleanupFallback = () => window.removeEventListener('resize', handleResize);
    }

    return () => {
      resizeObserver?.disconnect();
      cleanupFallback?.();
      rootEl.style.removeProperty('--youtube-mini-player-height');
    };
  }, [match]);

  if (!match) return null;

  return (
    <div ref={rootRef} className='youtube-mini-player'>
      <div className='youtube-mini-player__video'>
        <div className='youtube-mini-player__frame'>
          <div ref={containerRef} className='youtube-mini-player__frame-target' />
        </div>
        {showSpinner ? (
          <div className='youtube-mini-player__spinner'>
            <Spin size='small' />
          </div>
        ) : null}
        {autoplayBlocked ? (
          <button
            type='button'
            className='youtube-mini-player__overlay'
            onClick={handleAutoplayResume}
          >
            <PlayCircleOutlined />
            <span>Tap to play</span>
          </button>
        ) : null}
      </div>

      <div className='youtube-mini-player__info'>
        <div className='youtube-mini-player__header'>
          <div>
            <div className='youtube-mini-player__title'>{title}</div>
            <div className='youtube-mini-player__subtitle'>{timingLabel}</div>
          </div>
          <Tooltip title='Close mini-player'>
            <Button
              aria-label='Close YouTube mini-player'
              icon={<CloseOutlined />}
              onClick={handleClose}
              type='default'
              shape='circle'
              size='small'
              className='youtube-mini-player__close-button'
            />
          </Tooltip>
        </div>

        {error ? <div className='youtube-mini-player__error'>{error.message}</div> : null}

        <div className='youtube-mini-player__controls'>
          <Tooltip title={status === 'playing' ? 'Pause' : 'Play'}>
            <Button
              icon={status === 'playing' ? <PauseOutlined /> : <PlayCircleOutlined />}
              onClick={handleTogglePlay}
              disabled={status === 'loading'}
            />
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default YoutubeMiniPlayer;
